import { ApiClient } from "@hakili/api-client";
import {
  challenged,
  CODE_DEMANDE,
  type Identity,
  identityFrom,
  messageOf,
  reasonOf,
  refused,
  type Session,
  signedIn,
  signedOut,
  START,
} from "@hakili/app-shell";
import { t } from "@hakili/i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * `P1-BKW-001`: the banker's session, and it is the console's machine reused
 * rather than the console's machine copied.
 *
 * **What moved and what did not.** `auth-session.ts` , the transitions, the
 * identity parser, the bearer held in memory and nowhere else , moved into
 * `@hakili/app-shell` in round 189, because two apps writing that twice is two
 * chances to put a token in `localStorage` and the copy that drifts is always
 * the one nobody re-read. What stays here is the wiring an app owns: which
 * routes it calls, and what it does when they refuse.
 *
 * **No persona switching lives here.** `PersonaSwitcher` already exists behind
 * the `demo` flag and reads the shell store; the session answers only who the
 * caller IS. The bank's two personas are derived from the role the server
 * returns, which is `P1-BKW-001`'s own sentence: *"login and persona derivation
 * from the role"*.
 */

export interface BankSession {
  readonly session: Session;
  /**
   * `C-1` (BANK-V2, R-18): **the fourth login state, « Votre session a expiré. »** A live session whose refresh the api
   * refused (`ApiClient`'s `onSessionEnded`) is not a refusal of a password and not a deliberate sign-out: the banker was
   * in, and the next call told her she is out. It carries the e-mail she was signed in with, so the login screen keeps it and
   * she retypes the password and nothing else. `null` in every other state. No door is behind it: it is this hook's reading
   * of a refusal it already received.
   */
  readonly expired: { readonly email: string } | null;
  readonly connecter: (credentials: { email: string; password: string }) => Promise<void>;
  readonly deconnecter: () => Promise<void>;
  readonly api: ApiClient;
  /** Round `L-0`: « Une idée ? » , the product-feedback door, which admits any authenticated principal. */
  readonly submitFeatureRequest: (demande: {
    readonly faire: string;
    readonly manque: string;
    readonly permettrait: string;
    readonly contexte: string;
  }) => Promise<void>;
}

/**
 * The two personas of 03, 3.0, derived from the role and never chosen.
 *
 * A role this app does not know lands on the ANALYST, which is the narrower of
 * the two: 6.1.7 gives `voirPipelineEquipe` to the chef alone, so guessing
 * "chef" would show a screen the server would then refuse , and the failure
 * mode of guessing wrong is asymmetric. The server refuses in any case; this is
 * only which screen opens first.
 */
/**
 * **The two bank personas, and `null` for everything else.**
 *
 * The first cut mapped every unknown role to `analyste`, and the banker sitting
 * of 30 August showed what that costs: a Hakili `administration` principal
 * signing into `banque-staging` was titled **Analyste crédit** , this window
 * dressing Hakili staff in a bank's title. A persona derived by falling back is
 * a persona that can be wrong about who somebody is.
 *
 * 6.1.7's bank columns are `chefCredit` and `analysteCredit`, and they are the
 * whole list. Anything else is not this window's person, and the app says so
 * rather than guessing , the server refuses every bank route to them in any
 * case, so a guess buys nothing but a misleading header.
 */
export const personaDe = (role: string): "analyste" | "chef" | null => {
  if (role === "chefCredit") return "chef";
  if (role === "analysteCredit") return "analyste";
  return null;
};

export function useSession(origin: string): BankSession {
  // `onSessionEnded` fires from inside the client, which is built before the state it must set; it calls through a ref.
  const ended = useRef<() => void>(() => undefined);
  const api = useMemo(
    () =>
      new ApiClient({
        origin,
        surface: "bank",
        onSessionEnded: () => {
          ended.current();
        },
      }),
    [origin],
  );
  const [session, setSession] = useState<Session>(START);
  const [expired, setExpired] = useState<{ readonly email: string } | null>(null);
  const sessionNow = useRef<Session>(START);
  sessionNow.current = session;
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  // `C-1`: only a session that was LIVE can expire. A refused refresh at boot (no cookie) or while anonymous is the
  // ordinary answer for somebody who is not signed in, and says nothing.
  ended.current = (): void => {
    const now = sessionNow.current;
    if (!live.current || now.status !== "authenticated") return;
    setExpired({ email: now.identity.email });
    setSession(signedOut());
  };

  /**
   * **Reports by STATE and never by throwing**, which round 185 booked for the
   * client app after a rethrow reached nobody and surfaced as an unhandled
   * rejection in the founder's console. Every branch below ends in `setSession`.
   */
  const identify = useCallback(async (): Promise<void> => {
    try {
      const me = await api.request<unknown>("/api/v1/me", "get");
      const identity: Identity | null = identityFrom(me);
      if (identity !== null) setExpired(null);
      setSession(identity === null ? signedOut(t("bank.session.inattendue")) : signedIn(identity));
    } catch (failure) {
      setSession(signedOut(messageOf(failure, t("common.error.reseau"))));
    }
  }, [api]);

  /**
   * **On boot, try the cookie.** The access token was deliberately forgotten
   * when the page unloaded; the `SameSite=Strict` refresh cookie was not, and it
   * is same-site with this host by `A41`'s design. A `401` here is not an error
   * , it is the answer for somebody who is not signed in , which is why this
   * branch is silent by design.
   */
  useEffect(() => {
    void (async () => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/refresh", "post");
        if (!live.current) return;
        if (typeof tokens.accessToken === "string") {
          api.setAccessToken(tokens.accessToken);
          await identify();
          return;
        }
        setSession(signedOut());
      } catch {
        if (live.current) setSession(signedOut());
      }
    })();
  }, [api, identify]);

  const connecter = useCallback(
    async (credentials: { email: string; password: string; totpCode?: string }): Promise<void> => {
      try {
        const tokens = await api.request<{ accessToken?: string }>("/api/v1/auth/staff/login", "post", {
          body: credentials,
        });
        if (typeof tokens.accessToken !== "string") {
          setSession(refused(t("bank.session.inattendue")));
          return;
        }
        api.setAccessToken(tokens.accessToken);
        await identify();
      } catch (failure) {
        /**
         * `FX-3`, `M3` (`S2-2`): **the second factor's refusal is a different state from a wrong password.**
         *
         * `domain/login.ts` answers `forbidden` with `details.reason = "totp_code_required"` for a seat that
         * HAS enrolled (canon: optional for every role but `administration`, `DECISION ADDED 06-02`). Until
         * this round the bank's session folded it into `refused`, so the screen printed « Un code de
         * vérification est requis. » and had nowhere to type one , the sitting's `S2-2`, and the console's
         * own `P0-UIK-021` repeated on another surface. `challenged` is `app-shell`'s state for exactly this.
         */
        const message = messageOf(failure, t("common.error.reseau"));
        setSession(reasonOf(failure) === CODE_DEMANDE ? challenged(message) : refused(message));
      }
    },
    [api, identify],
  );

  const deconnecter = useCallback(async (): Promise<void> => {
    try {
      await api.request<unknown>("/api/v1/auth/logout", "post");
    } catch {
      // The server may already have forgotten this session. Either way the
      // client forgets its own token, which is the half that matters here.
    }
    api.setAccessToken(null);
    setExpired(null);
    setSession(signedOut());
  }, [api]);

  const submitFeatureRequest = useCallback(
    async (demande: {
      readonly faire: string;
      readonly manque: string;
      readonly permettrait: string;
      readonly contexte: string;
    }): Promise<void> => {
      // The door's own field names; `app` is the server's to infer from the surface header.
      await api.request<unknown>("/api/v1/feature-requests", "post", {
        body: {
          goal: demande.faire,
          missing: demande.manque,
          benefit: demande.permettrait,
          screenLabel: demande.contexte,
        },
      });
    },
    [api],
  );

  return useMemo(
    () => ({ session, expired, connecter, deconnecter, api, submitFeatureRequest }),
    [session, expired, connecter, deconnecter, api, submitFeatureRequest],
  );
}
